import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Boxes, Network, Layers, Plus, Search, SlidersHorizontal, Filter } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button, Input, Badge, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuCheckboxItem,
  Popover, PopoverTrigger, PopoverContent,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useK8sNetworks, useK8sNetworkSummary, useCreateK8sNetwork, useK8sResources, k8sNetworkCidr, type K8sNetwork, type K8sResource } from '@/api/k8s';
import { useOwnedZones } from '@/api/opennebula/zones';
import { ListPageShell, TableSkeleton, ErrorState } from '../compute/components/ListPageShell';
import { RowMenu } from '../components/RowMenu';
import { DescList, DescItem, monoValue } from '../components/DescList';

// ---- Constants -----------------------------------------------------------

const PAGE_SIZE = 20;

// Network columns the user can toggle via Display options (name + actions fixed).
const NETWORK_TOGGLEABLE = [
  { id: 'cidr', label: 'CIDR' },
  { id: 'type', label: 'Type' },
];
const NETWORK_DEFAULT_VISIBLE: Record<string, boolean> = { cidr: true, type: true };

// Resource columns the user can toggle via Display options (name + zone fixed).
const RESOURCE_TOGGLEABLE: { id: string; label: string }[] = [];

// ---- Shared helpers -------------------------------------------------------

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

// ---- K8sNetworks ---------------------------------------------------------

export function K8sNetworks() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: networks = [], isLoading, isError, refetch } = useK8sNetworks(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [summaryFor, setSummaryFor] = useState<K8sNetwork | null>(null);
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState<Record<string, boolean>>(NETWORK_DEFAULT_VISIBLE);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Collect distinct network types for the Filters popover.
  const networkTypes = useMemo(
    () => Array.from(new Set(networks.map((n) => n.type).filter(Boolean) as string[])).sort(),
    [networks],
  );

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    let rows = networks;
    if (q) rows = rows.filter((n) => (n.name || n.id).toLowerCase().includes(q) || (k8sNetworkCidr(n) || '').toLowerCase().includes(q));
    if (typeFilter) rows = rows.filter((n) => n.type === typeFilter);
    return rows;
  }, [networks, q, typeFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 on search/filter change.
  const handleSearch = (v: string) => { setQuery(v); setPage(1); };
  const handleTypeFilter = (v: string) => { setTypeFilter(v); setPage(1); };

  const columns = useMemo<ColumnDef<K8sNetwork, unknown>[]>(() => {
    const all: Record<string, ColumnDef<K8sNetwork, unknown>> = {
      cidr: { id: 'cidr', header: 'CIDR', cell: ({ row }) => muted(k8sNetworkCidr(row.original) || '—') },
      // Badge gives visual parity with the domain list's label style (neutral subtle tag).
      type: {
        id: 'type',
        header: 'Type',
        cell: ({ row }) =>
          row.original.type ? (
            <Badge tone="neutral" appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
              {row.original.type}
            </Badge>
          ) : muted('—'),
      },
    };
    const nameCol: ColumnDef<K8sNetwork, unknown> = {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span>,
    };
    const actionsCol: ColumnDef<K8sNetwork, unknown> = {
      id: 'actions',
      header: '',
      cell: ({ row }) => <RowMenu items={[{ label: 'IP usage', onSelect: () => setSummaryFor(row.original) }]} />,
    };
    return [nameCol, ...NETWORK_TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [visible]);

  const activeFilters = typeFilter ? 1 : 0;

  return (
    <ListPageShell
      icon={<Network size={22} />}
      breadcrumbs={
        <Breadcrumbs>
          <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
          <BreadcrumbItem current>Kubernetes networks</BreadcrumbItem>
        </Breadcrumbs>
      }
      title={`Kubernetes networks${networks.length ? ` (${networks.length})` : ''}`}
      description="Networks available to clusters in this service."
      // Verb aligned to "Create" convention used across the K8s console.
      actions={<Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Create network</Button>}
    >
      {isError ? (
        <ErrorState title="Couldn't load networks" description="The network list is temporarily unavailable." onRetry={() => refetch()} />
      ) : isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search by name or CIDR…"
                startSlot={<Search />}
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
            end={
              <>
                {/* Type filter — only shown when there are distinct types to filter by. */}
                {networkTypes.length > 0 && (
                  <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="secondary" startIcon={<Filter size={15} />}>
                        Filters{activeFilters > 0 ? ` (${activeFilters})` : ''}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" style={{ padding: 'var(--vn-space-3)', minWidth: 180 }}>
                      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-muted)' }}>
                          Type
                        </span>
                        {[{ value: '', label: 'All types' }, ...networkTypes.map((t) => ({ value: t, label: t }))].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { handleTypeFilter(opt.value); setFilterOpen(false); }}
                            style={{
                              textAlign: 'left',
                              background: typeFilter === opt.value ? 'var(--vn-bg-active)' : 'transparent',
                              border: 'none',
                              borderRadius: 'var(--vn-radius-sm)',
                              padding: 'var(--vn-space-1) var(--vn-space-2)',
                              cursor: 'pointer',
                              color: 'var(--vn-fg-default)',
                              fontSize: 'var(--vn-font-size-md)',
                              textTransform: 'capitalize',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {activeFilters > 0 && (
                  <Button variant="ghost" onClick={() => { setTypeFilter(''); setPage(1); }}>
                    Clear
                  </Button>
                )}
                {/* Display options column toggle — lets users show/hide CIDR and Type columns. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                      Display options
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                    {NETWORK_TOGGLEABLE.map((c) => (
                      <DropdownMenuCheckboxItem
                        key={c.id}
                        checked={visible[c.id]}
                        onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {c.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            }
          />
          <DataTable
            columns={columns}
            data={pageSlice}
            getRowId={(n) => String(n.id)}
            onRowClick={(n) => setSummaryFor(n)}
            emptyState={
              filtered.length === 0 && networks.length === 0 ? (
                <EmptyState icon={<Network size={20} />} title="No networks yet" description="No cluster networks found." />
              ) : (
                <EmptyState icon={<Network size={20} />} title="No matching networks" description="Try a different search term or filter." />
              )
            }
            footer={filtered.length > 0 ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}` : undefined}
          />
          {pageCount > 1 && (
            <div style={{ marginTop: 'var(--vn-space-4)' }}>
              <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
      {/* Create dialog uses "Create network" in the title for consistency with the action button. */}
      <K8sNetworkCreateDialog open={createOpen} onOpenChange={setCreateOpen} serviceId={serviceId!} onDone={() => show({ tone: 'success', title: 'Network created' })} />
      {summaryFor && <NetSummaryDialog serviceId={serviceId!} network={summaryFor} onClose={() => setSummaryFor(null)} />}
    </ListPageShell>
  );
}

/** IP usage is fetched on demand (the summary endpoint 500s for some networks). */
function NetSummaryDialog({ serviceId, network, onClose }: { serviceId: string; network: K8sNetwork; onClose: () => void }) {
  const { data, isLoading, isError } = useK8sNetworkSummary(serviceId, network.id);
  const leased = Number(data?.leased ?? data?.attached?.length ?? 0);
  const size = Number(data?.size ?? 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{network.name || network.id} — IP usage</DialogTitle></DialogHeader>
        <DialogBody>
          {isLoading ? <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>Loading…</p> : isError || !data ? (
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)' }}>Usage summary is not available for this network.</p>
          ) : (
            <DescList minColumn={140} style={{ gap: 'var(--vn-space-3)' }}>
              <DescItem label="CIDR"><code style={monoValue}>{k8sNetworkCidr(network) || '—'}</code></DescItem>
              <DescItem label="Total IPs">{size || '—'}</DescItem>
              <DescItem label="Leased">{leased}</DescItem>
              <DescItem label="Available">{size ? size - leased : '—'}</DescItem>
            </DescList>
          )}
        </DialogBody>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function K8sNetworkCreateDialog({ open, onOpenChange, serviceId, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; serviceId: string; onDone: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const create = useCreateK8sNetwork(serviceId, {
    onSuccess: () => { onDone(); onOpenChange(false); setName(''); setPrefix(''); },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Title aligned with "Create network" button verb. */}
        <DialogHeader><DialogTitle>Create network</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="cluster-net" />}</Field>
            <Field label="CIDR (prefix)">{(p) => <Input {...p} value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="10.20.0.0/24" />}</Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!name.trim() || !prefix.trim() || create.isPending} onClick={() => create.mutate({ name: name.trim(), prefix: prefix.trim() })}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- K8sResources --------------------------------------------------------

export function K8sResources() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: resources = [], isLoading, isError, refetch } = useK8sResources(serviceId);
  // Zone UUID → name map from owned zones; falls back to "—" when not resolvable.
  const { ownedZones } = useOwnedZones();
  const zoneNameById = useMemo(
    () => Object.fromEntries(ownedZones.map((z) => [z.id, z.name])),
    [ownedZones],
  );

  const [query, setQuery] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState<Record<string, boolean>>({});

  // Collect distinct resolved zone names for the Filters popover.
  const zoneOptions = useMemo(
    () =>
      Array.from(
        new Set(
          resources
            .map((r) => (r.zone ? (zoneNameById[r.zone] ?? r.zone) : null))
            .filter(Boolean) as string[],
        ),
      ).sort(),
    [resources, zoneNameById],
  );

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    let rows = resources;
    if (q) {
      rows = rows.filter((r) => {
        const name = (r.service?.service_name || '').toLowerCase();
        const zone = (r.zone ? (zoneNameById[r.zone] ?? r.zone) : '').toLowerCase();
        return name.includes(q) || zone.includes(q);
      });
    }
    if (zoneFilter) {
      rows = rows.filter((r) => {
        const resolvedZone = r.zone ? (zoneNameById[r.zone] ?? r.zone) : '';
        return resolvedZone === zoneFilter;
      });
    }
    return rows;
  }, [resources, q, zoneFilter, zoneNameById]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSlice = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (v: string) => { setQuery(v); setPage(1); };
  const handleZoneFilter = (v: string) => { setZoneFilter(v); setPage(1); };

  // resource.id is an opaque resource-group descriptor UUID, NOT a cluster id.
  // service.service_name is the human-readable cluster name; service_uuid is the
  // cluster's UUID. Showing resource.id as "Cluster ID" would expose a raw UUID
  // with no user-facing meaning — drop it entirely and show the cluster name only.
  const columns = useMemo<ColumnDef<K8sResource, unknown>[]>(() => {
    return [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.service?.service_name || '—'}
          </span>
        ),
      },
      {
        id: 'zone',
        header: 'Zone',
        // Zone UUID resolved to a human name; falls back to "—" (never raw UUID).
        cell: ({ row }) => muted(row.original.zone ? (zoneNameById[row.original.zone] ?? '—') : '—'),
      },
    ];
  }, [zoneNameById]);

  // RESOURCE_TOGGLEABLE is empty — no columns to hide/show beyond name+zone.
  // Display options button is omitted when there are no toggleable columns.

  const activeFilters = zoneFilter ? 1 : 0;

  return (
    <ListPageShell
      icon={<Layers size={22} />}
      breadcrumbs={
        <Breadcrumbs>
          <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
          <BreadcrumbItem current>Kubernetes resources</BreadcrumbItem>
        </Breadcrumbs>
      }
      title={`Kubernetes resources${resources.length ? ` (${resources.length})` : ''}`}
      description="Workloads and objects across clusters."
    >
      {isError ? (
        <ErrorState title="Couldn't load resources" description="The resource list is temporarily unavailable." onRetry={() => refetch()} />
      ) : isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search by name or zone…"
                startSlot={<Search />}
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
            end={
              <>
                {/* Zone filter — only shown when there are distinct zones to filter by. */}
                {zoneOptions.length > 0 && (
                  <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="secondary" startIcon={<Filter size={15} />}>
                        Filters{activeFilters > 0 ? ` (${activeFilters})` : ''}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" style={{ padding: 'var(--vn-space-3)', minWidth: 180 }}>
                      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-muted)' }}>
                          Zone
                        </span>
                        {[{ value: '', label: 'All zones' }, ...zoneOptions.map((z) => ({ value: z, label: z }))].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { handleZoneFilter(opt.value); setFilterOpen(false); }}
                            style={{
                              textAlign: 'left',
                              background: zoneFilter === opt.value ? 'var(--vn-bg-active)' : 'transparent',
                              border: 'none',
                              borderRadius: 'var(--vn-radius-sm)',
                              padding: 'var(--vn-space-1) var(--vn-space-2)',
                              cursor: 'pointer',
                              color: 'var(--vn-fg-default)',
                              fontSize: 'var(--vn-font-size-md)',
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {activeFilters > 0 && (
                  <Button variant="ghost" onClick={() => { setZoneFilter(''); setPage(1); }}>
                    Clear
                  </Button>
                )}
                {/* Display options — RESOURCE_TOGGLEABLE is empty so this button is
                    omitted; re-enable if new optional columns are added in future. */}
                {RESOURCE_TOGGLEABLE.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                        Display options
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                      {RESOURCE_TOGGLEABLE.map((c) => (
                        <DropdownMenuCheckboxItem
                          key={c.id}
                          checked={visible[c.id]}
                          onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                          onSelect={(e) => e.preventDefault()}
                        >
                          {c.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            }
          />
          {/* Empty placeholder renders inside the table's bordered frame via `emptyState`
              so the layout stays consistent whether the list is empty or populated. */}
          <DataTable
            columns={columns}
            data={pageSlice}
            getRowId={(r, i) => String(r.id ?? i)}
            emptyState={
              filtered.length === 0 && resources.length === 0 ? (
                <EmptyState icon={<Boxes size={20} />} title="No resources yet" description="No resources reported." />
              ) : (
                <EmptyState icon={<Boxes size={20} />} title="No matching resources" description="Try a different search term or filter." />
              )
            }
            footer={filtered.length > 0 ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}` : undefined}
          />
          {pageCount > 1 && (
            <div style={{ marginTop: 'var(--vn-space-4)' }}>
              <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </ListPageShell>
  );
}
